import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, AlertTriangle, Video } from "lucide-react";
import { api } from "@/lib/api";
import {
  EXAM_LABELS,
  SUBJECT_LABELS,
  DIFFICULTY_LABELS,
  difficultyClass,
  subjectAccent,
} from "@/lib/pyqMeta";
import { MathText } from "@/components/MathText";
import { BookmarkButton } from "@/components/BookmarkButton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export default function Mistakes() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [bookmarks, setBookmarks] = useState(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/mistakes").then((r) => setItems(r.data.items)).finally(() => setLoading(false));
    api.get("/bookmarks/ids").then((r) => setBookmarks(new Set(r.data))).catch(() => {});
  }, []);

  const toggleBookmark = (id, on) =>
    setBookmarks((prev) => {
      const n = new Set(prev);
      if (on) n.add(id);
      else n.delete(id);
      return n;
    });

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">Revise Mistakes</h1>
          <p className="mt-2 text-muted-foreground">
            Every question you last answered wrong — practise until you nail them.
          </p>
        </div>
        <Button
          data-testid="practise-mistakes-btn"
          onClick={() => navigate("/student/session?mistakes=1&mode=UNTIMED")}
          disabled={loading || items.length === 0}
          className="rounded-full px-6"
        >
          <Play className="mr-1.5 h-4 w-4" /> Practise mistakes ({items.length})
        </Button>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-28 w-full rounded-2xl" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-14 text-center" data-testid="mistakes-empty">
          <AlertTriangle className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
          <p className="font-heading text-lg font-semibold">No mistakes to revise</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Questions you answer incorrectly will show up here automatically. Keep practising!
          </p>
          <Button asChild variant="outline" className="mt-6 rounded-full">
            <a href="/student/practice/pyq">Start practising</a>
          </Button>
        </div>
      ) : (
        <div className="space-y-4" data-testid="mistakes-list">
          {items.map((q, idx) => (
            <motion.div
              key={q.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: Math.min(idx * 0.03, 0.3) }}
              data-testid={`mistake-card-${q.id}`}
              className="group rounded-2xl border border-border bg-card p-5 transition-colors hover:border-red-500/40"
            >
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="rounded-full border-red-500/40 font-medium text-red-600 dark:text-red-400">
                  <AlertTriangle className="mr-1 h-3 w-3" /> Got wrong
                </Badge>
                <Badge variant="outline" className="rounded-full border-border font-medium">
                  {EXAM_LABELS[q.examType] || q.examType}
                </Badge>
                <span className={cn("text-xs font-semibold uppercase tracking-wide", subjectAccent(q.subject))}>
                  {SUBJECT_LABELS[q.subject] || q.subject}
                </span>
                <Badge className={cn("rounded-full border", difficultyClass(q.difficulty))}>
                  {DIFFICULTY_LABELS[q.difficulty]}
                </Badge>
                <span className="text-xs text-muted-foreground">{q.chapter}</span>
                {q.videoEligible && (
                  <Badge data-testid={`video-badge-${q.id}`} className="rounded-full border-primary/30 bg-primary/10 text-[10px] text-primary hover:bg-primary/15">
                    <Video className="mr-1 h-3 w-3" /> Video solution
                  </Badge>
                )}
                <BookmarkButton
                  questionId={q.id}
                  active={bookmarks.has(q.id)}
                  onChange={toggleBookmark}
                  size="sm"
                  className="ml-auto"
                />
              </div>
              <MathText
                block
                className="line-clamp-3 text-[15px] leading-relaxed text-foreground/90"
                text={q.questionText}
              />
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
